/**
 * Reglas de dominio sobre el TEXTO que devuelve el modelo
 * (docs/AI_ARCHITECTURE.md §8, ultimo parrafo).
 *
 * El §8 exige una cadena de cuatro pasos —schema de la API, schema en el
 * servidor, reglas de dominio, limites de seguridad— y hasta ahora los
 * propositos de REDACCION solo tenian los dos primeros. Los esquemas de
 * `contracts/ai.ts` comprueban que hay un `headline` de entre 10 y 160
 * caracteres; no comprueban **que hay dentro**.
 *
 * QUE SE COMPRUEBA AQUI, Y POR QUE SOLO ESO
 *
 * Lo que el §8 nombra literalmente: «sin HTML, sin enlaces activos». Son las
 * dos cosas que un modelo puede colar y que no son opinables:
 *
 *   - **HTML.** React escapa lo que interpola, asi que hoy no hay inyeccion
 *     posible; pero «hoy no hay» no es una regla, es una circunstancia. El dia
 *     que alguien escriba `dangerouslySetInnerHTML` o el texto se exporte a un
 *     PDF, la circunstancia cambia y la regla sigue.
 *   - **Enlaces.** Una URL en la explicacion de por que subes de 12 a 14
 *     series no puede venir de los datos: el contexto no lleva ninguna. Solo
 *     puede estar inventada, y una referencia inventada es exactamente el
 *     fallo que `ROL-PREPARADOR.md` prohibe.
 *   - **Correos.** Por lo mismo, y ademas porque un correo en el texto de
 *     vuelta es la señal de que algo identificativo dio la vuelta entera.
 *
 * LO QUE NO SE COMPRUEBA, Y TAMPOCO SE FINGE
 *
 * Que no invente numeros, que no diagnostique, que no prometa resultados. Son
 * las reglas que mas importan y **ninguna es comprobable con una expresion
 * regular**: viven en el prompt (§9) y, para lo que de verdad decide algo, en
 * el hecho de que la IA no decide nada. Poner aqui un filtro de palabras
 * daria una sensacion de cobertura que no existe.
 *
 * El veredicto es BINARIO a proposito: no se recorta el texto ni se le quitan
 * las etiquetas. Un parrafo saneado a medias es un parrafo que ya no dice lo
 * que su autor escribio, y para eso esta la version determinista, que dice lo
 * correcto y no hay que arreglarla.
 */

/** Una etiqueta HTML: `<b>`, `</p>`, `<img src=…>`. */
const HTML = /<\/?[a-z][^>]*>/i;

/** Un enlace, escrito como URL, como dominio suelto o en markdown. */
const ENLACE = /(https?:\/\/|www\.[a-z0-9-]+\.[a-z]{2,}|\]\([^)]*\))/i;

const CORREO = /[\w.+-]+@[\w-]+\.[\w.]+/;

const REGLAS: readonly { patron: RegExp; motivo: string }[] = [
  { patron: HTML, motivo: 'html' },
  { patron: ENLACE, motivo: 'enlace' },
  { patron: CORREO, motivo: 'correo' },
];

export interface ProseVerdict {
  accepted: boolean;
  /** Vacio si `accepted`. Se registra; no se enseña al usuario. */
  violations: string[];
}

/**
 * Juzga los campos de texto de una salida de IA.
 *
 * Recibe los fragmentos ya extraidos por quien llama —no el objeto— para que
 * este modulo no tenga que conocer la forma de cada proposito. Si un dia hay
 * un cuarto esquema, aqui no se toca nada.
 */
export function validateAIProse(fragments: readonly string[]): ProseVerdict {
  const violations: string[] = [];

  for (const fragmento of fragments) {
    for (const { patron, motivo } of REGLAS) {
      if (patron.test(fragmento) && !violations.includes(motivo)) violations.push(motivo);
    }
  }

  return { accepted: violations.length === 0, violations };
}

/**
 * Los fragmentos de texto de una salida, sea cual sea su proposito.
 *
 * Recorre el objeto y se queda con las cadenas. Es deliberadamente generico:
 * un campo de texto nuevo en cualquier esquema queda cubierto **el dia que se
 * añade**, sin que nadie tenga que acordarse de venir aqui. Lo contrario —una
 * lista de nombres de campo— fallaria en silencio y por omision, que es la
 * peor forma de fallar que tiene una comprobacion de seguridad.
 *
 * HAY UNA EXCEPCION, Y SE DECLARA AQUI PARA QUE NO SE OLVIDE (22/09/2026)
 *
 * `clavesDeDatos` son campos cuyo contenido **es un dato, no prosa**, y cuya
 * procedencia comprueba otra regla mas fuerte. El unico caso hoy es
 * `evidence_review`: una ficha de literatura lleva la `url` del documento, y
 * `retrievedUrls` es la lista que las herramientas del servidor descargaron de
 * verdad. Sin esta excepcion, la regla «ningun enlace en el texto» rechazaba la
 * revision ENTERA por contener exactamente lo que el §8 le pide que traiga: una
 * referencia verificable. Medido con una revision de 27,6 centimos que buscó,
 * encontró cuatro metaanálisis y no guardó ni uno.
 *
 * Lo que protege el hueco: esas URL pasan por `validateRetrieved`, que las
 * rechaza si no estan entre las que la API descargo. Es MAS estricto que
 * «no parece un enlace», no menos: una URL inventada es imposible, no
 * improbable. La prosa de la ficha —`findings`, `limitations`, `population`—
 * sigue pasando por aqui entera.
 */
export function proseFragmentsOf(
  payload: unknown,
  clavesDeDatos: ReadonlySet<string> = new Set(),
): string[] {
  if (payload === null || typeof payload !== 'object') {
    return typeof payload === 'string' ? [payload] : [];
  }
  if (Array.isArray(payload)) {
    return payload.flatMap((valor) => proseFragmentsOf(valor, clavesDeDatos));
  }
  return Object.entries(payload as Record<string, unknown>).flatMap(([clave, valor]) =>
    clavesDeDatos.has(clave) ? [] : proseFragmentsOf(valor, clavesDeDatos),
  );
}

/**
 * Los campos de `evidence_review` que son datos y no prosa. Ver arriba.
 *
 * Vive en el dominio y no en el cliente por lo de siempre: una regla que hay
 * que acordarse de pasar en cada llamada es una regla que un dia no se pasa.
 */
export const CLAVES_DE_DATOS_DE_EVIDENCIA: ReadonlySet<string> = new Set([
  'url',
  'retrievedUrls',
]);
